import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { candidateApi } from '../services/recruitingApi';
import type { CandidateSourceType } from '../services/recruitingApi';
import { getCandidateFilterFacetsApiV1RecruitingCandidatesFilterFacetsGet } from '../../../generated/api/recruiting/recruiting';

export interface ListCandidatesParams {
  candidate_source?: CandidateSourceType;
  agency_organization_id?: string;
  referred_by_employee_id?: string;
  /** Comma-separated tag UUIDs (cross-cutting tags framework). */
  tags?: string;
  /** Tag matching mode: `any` (OR, default) or `all` (AND). */
  tags_mode?: 'any' | 'all';
  search?: string;
  has_applications?: boolean;
  /** A key of CANDIDATE_SORT_FIELDS (candidate_repository.py). Omit for the
   * endpoint's natural order — search relevance while searching, newest first
   * otherwise; naming a column here discards that relevance ordering. */
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  /** Admin list-scoping opt-out (§4.1): `'own'` narrows a Full-Access admin to their
   * own owner/shared candidates instead of the audited override view. Set from
   * `useAdminListScope`; undefined for everyone else. */
  admin_scope?: string;
}

export interface CandidateCreate {
  email?: string;
  first_name: string;
  last_name: string;
  phone?: string;
  current_title?: string;
  current_company?: string;
  years_experience?: number;
  expected_salary?: number;
  expected_salary_currency?: string;
  expected_salary_period?: string;
  linkedin_url?: string;
  twitter_url?: string;
  portfolio_url?: string;
  github_url?: string;
  city?: string;
  state?: string;
  country?: string;
  willing_to_relocate?: boolean;
  candidate_source?: CandidateSourceType;
  agency_organization_id?: string;
  referred_by_employee_id?: string;
  /** Cross-cutting tags framework — full-replacement set of tag UUIDs. */
  tag_ids?: string[];
  resume_file_id?: string;
}

export type CandidateUpdate = Partial<CandidateCreate>;

export const candidateKeys = {
  all: ['candidates'] as const,
  list: (params?: ListCandidatesParams) => [...candidateKeys.all, 'list', params] as const,
  detail: (id: string) => [...candidateKeys.all, 'detail', id] as const,
  filterFacets: (adminScope?: string, activeCompanyId?: string | null) =>
    [...candidateKeys.all, 'filter-facets', adminScope, activeCompanyId] as const,
};

export function useCandidateFilterFacets(adminScope?: string, activeCompanyId?: string | null) {
  return useQuery({
    queryKey: candidateKeys.filterFacets(adminScope, activeCompanyId),
    queryFn: ({ signal }) =>
      getCandidateFilterFacetsApiV1RecruitingCandidatesFilterFacetsGet(
        { admin_scope: adminScope },
        signal,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCandidates(params?: ListCandidatesParams) {
  return useQuery({
    queryKey: candidateKeys.list(params),
    // Search is in the query key: hold rows while the next result loads (ui-list-pages.md § 2).
    placeholderData: keepPreviousData,
    queryFn: () => candidateApi.list(params),
  });
}

export function useCandidate(id: string, options?: { include_applications?: boolean }) {
  return useQuery({
    queryKey: candidateKeys.detail(id),
    queryFn: () => candidateApi.get(id, options),
    enabled: !!id,
  });
}

export function useCreateCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CandidateCreate) => candidateApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.all });
    },
  });
}

export function useUpdateCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CandidateUpdate }) => candidateApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: candidateKeys.all });
    },
  });
}

export function useDeleteCandidate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => candidateApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: candidateKeys.all });
    },
  });
}
